import {
  Button,
  Input,
  Textarea,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@rakazo/ui-web";
import { AgentIdentityBackup, AgentSignedProof } from "@rakazo/contracts";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSpaceRpc } from "../lib/space-rpc";

import { downloadJson as downloadIdentityJson } from "../lib/download-json";
export { downloadIdentityJson };
export function AgentIdentitySettings({ botId }: { botId: string }) {
  const rpc = useSpaceRpc(),
    [mode, setMode] = useState<
      "backup" | "import" | "rotate" | "challenge" | "verify" | null
    >(null);
  const [password, setPassword] = useState(""),
    [confirm, setConfirm] = useState(""),
    [input, setInput] = useState(""),
    [expected, setExpected] = useState("");
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [result, setResult] = useState("");
  const state = useQuery({
    queryKey: ["agent-identity", botId, rpc],
    queryFn: () => rpc.agentIdentities.status({ botId }),
    retry: false,
  });
  const identity = state.data;
  function open(next: typeof mode) {
    setMode(next);
    setPassword("");
    setConfirm("");
    setInput("");
    setExpected("");
    setError("");
    setResult("");
  }
  async function act() {
    if (!mode || busy || !identity) return;
    setBusy(true);
    setError("");
    setResult("");
    try {
      const target = { botId, expectedPublicKey: identity.publicKey };
      if (mode === "backup") {
        if (password.length < 12 || password !== confirm)
          throw new Error(
            "Use a passphrase of at least 12 characters and enter it twice.",
          );
        downloadIdentityJson(
          await rpc.agentIdentities.export({ ...target, passphrase: password }),
          "wurkees-identity-encrypted.json",
        );
        open(null);
      } else if (mode === "import") {
        await rpc.agentIdentities.import({
          ...target,
          passphrase: password,
          backup: AgentIdentityBackup.parse(JSON.parse(input)),
        });
        open(null);
        await state.refetch();
      } else if (mode === "rotate") {
        await rpc.agentIdentities.rotate(target);
        open(null);
        await state.refetch();
      } else if (mode === "challenge") {
        downloadIdentityJson(
          await rpc.agentIdentities.challenge({ botId, challenge: input }),
          "wurkees-identity-proof.json",
        );
        setResult("Verification proof downloaded.");
      } else {
        const verification = await rpc.agentIdentities.verify({
          expectedPublicKey: expected,
          proof: AgentSignedProof.parse(JSON.parse(input)),
        });
        setResult(verification.reason);
      }
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Could not update the identity.",
      );
    } finally {
      setBusy(false);
      setPassword("");
      setConfirm("");
    }
  }
  return (
    <details
      className="mt-5 border-t border-border pt-4"
      data-testid="agent-identity-settings"
    >
      <summary className="cursor-pointer text-sm font-medium">
        Agent identity
      </summary>
      <div className="mt-4 space-y-3">
        {state.isError ? (
          <p role="alert" className="text-sm text-destructive">
            Could not load the signing identity.
          </p>
        ) : identity ? (
          <>
            <label className="block text-sm">
              Public identity · version {identity.version}
              <Input
                className="mt-1 font-mono text-xs"
                readOnly
                value={identity.npub}
                aria-label="Agent public identity"
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() =>
                  void navigator.clipboard.writeText(identity.npub)
                }
              >
                Copy public key
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() =>
                  downloadIdentityJson(
                    {
                      format: "wurkees-public-identity",
                      formatVersion: 1,
                      ...identity,
                    },
                    "wurkees-public-identity.json",
                  )
                }
              >
                Public identity file
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => open("backup")}
              >
                Encrypted backup
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => open("import")}
              >
                Import identity
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => open("challenge")}
              >
                Sign challenge
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => open("verify")}
              >
                Verify proof
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => open("rotate")}
              >
                Replace key
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Nostr-compatible signing. Relay publishing is off. Signatures
              verify a key and signed text; they do not grant permissions.
            </p>
            {identity.previous.length > 0 ? (
              <details>
                <summary className="cursor-pointer text-xs">
                  Previous keys ({identity.previous.length})
                </summary>
                {identity.previous.map((k) => (
                  <p
                    key={k.publicKey}
                    className="mt-2 break-all font-mono text-xs"
                  >
                    Version {k.version} · {k.npub}
                  </p>
                ))}
              </details>
            ) : null}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Loading identity…</p>
        )}
      </div>
      <Dialog
        open={mode !== null}
        onOpenChange={(value) => {
          if (!value && !busy) open(null);
        }}
      >
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {mode === "backup"
                ? "Encrypted identity backup"
                : mode === "import"
                  ? "Import identity"
                  : mode === "rotate"
                    ? "Replace signing key"
                    : mode === "challenge"
                      ? "Sign verification challenge"
                      : "Verify signed proof"}
            </DialogTitle>
            <DialogDescription>
              {mode === "backup"
                ? "This file can impersonate this agent when unlocked. Choose a unique passphrase and store it separately. It contains the signing identity only."
                : mode === "import"
                  ? "Replace this bot's current identity with an encrypted NIP-49 backup. The previous key is retired. Chats, model access and permissions are separate."
                  : mode === "rotate"
                    ? "Create a new identity and retire the current key. The old private key is removed from active storage. Keep a backup first if you need it elsewhere. Previous signatures remain verifiable."
                    : mode === "challenge"
                      ? "Sign a challenge supplied by another app. This proves control of this agent's key and does not authorize actions."
                      : "Use an expected public key you obtained independently. A matching signature does not prove the message is true or authorized."}
            </DialogDescription>
          </DialogHeader>
          {mode === "import" ? (
            <label className="text-sm">
              Encrypted backup file
              <Input
                type="file"
                accept=".json,application/json"
                disabled={busy}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) {
                    if (file.size > 8192) {
                      setError("The backup file is too large.");
                      return;
                    }
                    void file.text().then(setInput);
                  }
                }}
              />
            </label>
          ) : null}
          {mode === "challenge" || mode === "verify" ? (
            <label className="text-sm">
              {mode === "challenge"
                ? "Challenge (16 characters minimum)"
                : "Signed proof JSON"}
              <Textarea
                className="mt-1"
                rows={5}
                maxLength={300000}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={busy}
              />
            </label>
          ) : null}
          {mode === "verify" ? (
            <label className="text-sm">
              Expected npub or public key
              <Input
                value={expected}
                onChange={(e) => setExpected(e.target.value)}
                disabled={busy}
              />
            </label>
          ) : null}
          {mode === "backup" || mode === "import" ? (
            <label className="text-sm">
              Passphrase
              <Input
                type="password"
                autoComplete="new-password"
                value={password}
                maxLength={256}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
              />
            </label>
          ) : null}
          {mode === "backup" ? (
            <label className="text-sm">
              Repeat passphrase
              <Input
                type="password"
                autoComplete="new-password"
                value={confirm}
                maxLength={256}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={busy}
              />
            </label>
          ) : null}
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
          {result ? (
            <p role="status" className="text-sm">
              {result}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => open(null)}
            >
              Cancel
            </Button>
            <Button type="button" disabled={busy} onClick={() => void act()}>
              {busy
                ? "Working…"
                : mode === "backup"
                  ? "Download encrypted backup"
                  : mode === "import"
                    ? "Import and replace"
                    : mode === "rotate"
                      ? "Replace key"
                      : mode === "challenge"
                        ? "Download signed proof"
                        : "Verify"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </details>
  );
}
